import {
	ApButtonMain,
	ApButtonText,
	ApIcon,
	colors,
} from '@alixpartners/ui-components';
import { ApSignalrConsumer, Telemetry } from '@alixpartners/ui-utils';
import React from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import {
	ButtonWrapper,
	DescriptionText,
	DropWrapper,
	DropZoneArea,
	DropZoneText,
	IconWrapper,
	RefreshStatusBlock,
	Text,
} from './styles';
import { ETL_PROCESS_STATUS, VENDOR_UPLOAD_STATUS } from '../../constants';
import ErrorList from '../ErrorList';
import { cleanVendorState, validateFirstVendorFile } from 'utils/apiRequests';
import UploadProgressBar from '../UploadProgressBar';
import { getStatusMessage } from './utils';
import { GenerateSpendCubeContext } from 'contexts/GenerateSpendCubeContext';
import { SPEND_TYPE } from '../../constants';

const errorPoints = [
	VENDOR_UPLOAD_STATUS.UPLOADFAILED,
	VENDOR_UPLOAD_STATUS.VALIDATIONFAILED,
	VENDOR_UPLOAD_STATUS.FILEFORMATERROR,
	VENDOR_UPLOAD_STATUS.ILLEGALCHARACTERERROR,
	VENDOR_UPLOAD_STATUS.TRANSLATIONFAILED,
	VENDOR_UPLOAD_STATUS.PREPROCESSINGFAILED,
	VENDOR_UPLOAD_STATUS.DEDUPFAILED,
	VENDOR_UPLOAD_STATUS.FILEGENERATIONFAILED,
	VENDOR_UPLOAD_STATUS.PUBLISHFAILED,
	VENDOR_UPLOAD_STATUS.UNEXCEPTED_ERROR,
];

class Index extends React.Component {
	static propTypes = {
		status: PropTypes.object.isRequired,
		refetchVendorState: PropTypes.func.isRequired,
		fetchFileArchive: PropTypes.func.isRequired,
	};

	static contextType = GenerateSpendCubeContext;

	dropzoneRef = React.createRef();

	state = {
		statusMessage: '',
		status: this.props.status,
		errors: this.props.status.errors || [],
		currentNormalizationStep: this.props.status.state,
		refetchVendorState: this.props.refetchVendorState,
		totalNormalizationSteps: 21,
		isProcessing: this.props.status.state !== VENDOR_UPLOAD_STATUS.NO_DATA,
		isAnyErrorOccured: errorPoints.includes(Number(this.props.status.state)),
	};

	componentDidMount() {
		const { status } = this.props;
		const currentProgressMessage = getStatusMessage(Number(status.state));
		this.setState({
			isProcessing: Number(status.state) === 1 ? false : true,
			currentNormalizationStep: status.state,
			statusMessage: currentProgressMessage,
		});
		if (Number(status.state) === 21 || Number(status.state) === 0) {
			this.fetchFileAndCleanup();
		}
	}

	fetchFileAndCleanup() {
		const { fetchFileArchive, refetchVendorState } = this.props;
		this.setState({
			statusMessage: 'File published successfully.',
			currentNormalizationStep: 21,
			totalNormalizationSteps: 21,
		});
		fetchFileArchive();
		cleanVendorState().then(() => {
			refetchVendorState();
		});
	}

	onFileSelected = acceptedFiles => {
		const file = acceptedFiles[0];
		const currentProgressMessage = getStatusMessage(
			VENDOR_UPLOAD_STATUS.UPLOADING
		);
		const currentFlag = window.location.pathname.split('/')[3];
		const isDirect = currentFlag === SPEND_TYPE.DIRECT;
		this.setState(
			{
				statusMessage: currentProgressMessage,
				currentNormalizationStep: VENDOR_UPLOAD_STATUS.UPLOADING,
				isProcessing: true,
			},
			() => {
				validateFirstVendorFile(file, isDirect)
					.then(response => {
						const isValid = !response.errors || response.errors.length === 0;
						if (isValid) {
							const currentProgressMessage = getStatusMessage(response.state);
							this.setState({
								currentNormalizationStep: response.state,
								statusMessage: currentProgressMessage,
								isProcessing: true,
							});
						} else {
							this.setState({
								currentNormalizationStep: VENDOR_UPLOAD_STATUS.VALIDATIONFAILED,
							});
						}
					})
					.catch(() => {
						this.setState({
							currentNormalizationStep: VENDOR_UPLOAD_STATUS.VALIDATIONFAILED,
						});
					});
			}
		);
		Telemetry.trackEvent('file selected');
	};

	restartUploading(restartETL = false) {
		const { refetchVendorState } = this.props;
		cleanVendorState().then(() =>
			this.setState(
				{
					currentNormalizationStep: VENDOR_UPLOAD_STATUS.NO_DATA,
					isProcessing: false,
				},
				() => refetchVendorState()
			)
		);
		if (restartETL) {
			this.context.restart();
		}
	}

	renderInitialBlock = () => {
		Telemetry.trackEvent('showing upload file');

		return (
			<React.Fragment>
				<IconWrapper>
					<ApIcon
						iconName="cloud_upload"
						iconSize="96px"
						iconColor={colors.grey3}
					/>
				</IconWrapper>
				<DropZoneText>
					<DescriptionText>
						Drag <span>.csv</span> file here or browse files to upload
					</DescriptionText>
					<ButtonWrapper>
						<ApButtonMain iconName="baseline_add">Add file</ApButtonMain>
					</ButtonWrapper>
				</DropZoneText>
			</React.Fragment>
		);
	};

	renderProcessingBlock = () => {
		const {
			statusMessage,
			currentNormalizationStep,
			totalNormalizationSteps,
			refetchVendorState,
		} = this.state;
		const { fileName, fileSize, rowsWithErrors, errors } = this.props.status;
		const { fetchFileArchive } = this.props;

		return (
			<>
				<UploadProgressBar
					timeRequired
					label={statusMessage}
					value={Math.floor(
						(100 * currentNormalizationStep) / totalNormalizationSteps
					)}
				/>

				<ApSignalrConsumer
					message="NormalizationInProgress"
					onMessage={(
						messageKey,
						statusMessage,
						currentSignalRNormalizationStep,
						totalNormalizationSteps
					) => {
						if (Number(currentSignalRNormalizationStep) === 0) {
							this.restartUploading();
						} else {
							this.setState(
								{
									statusMessage: statusMessage,
									currentNormalizationStep: Number(
										currentSignalRNormalizationStep
									),
									totalNormalizationSteps: Number(totalNormalizationSteps),
								},
								() => {
									if (
										Number(currentSignalRNormalizationStep) ===
										VENDOR_UPLOAD_STATUS.PUBLISHED
									) {
										this.restartUploading();
									} else {
										refetchVendorState();
									}
								}
							);
						}
					}}
				/>
				<ApSignalrConsumer
					message="NormalizationCompleted"
					onMessage={() => {
						fetchFileArchive();
						this.restartUploading();
					}}
				/>
				<ApSignalrConsumer
					message="VendorIntegrationErrors"
					onMessage={() => {
						fetchFileArchive();
						this.restartUploading();
					}}
				/>
				{errorPoints.includes(Number(currentNormalizationStep)) && (
					<>
						<RefreshStatusBlock>
							<ApButtonText onClick={() => this.restartUploading(true)}>
								Restart Uploading
							</ApButtonText>
						</RefreshStatusBlock>
					</>
				)}
				{errors !== null &&
					errors.length > 0 && (
						<ErrorList
							rowsWithErrors={rowsWithErrors}
							errors={errors}
							fileSize={fileSize}
							fileName={fileName}
						/>
					)}
			</>
		);
	};

	render() {
		const { isProcessing, currentNormalizationStep } = this.state;
		if (isProcessing) {
			return this.renderProcessingBlock();
		}
		return (
			<React.Fragment>
				{this.context.currentState ===
					ETL_PROCESS_STATUS.NORMALIZATION_SUCCEEDED ||
				this.context.isShowRedirectionMessage ? (
					<DropZoneArea>
						{this.context.isETL === 1 ? (
							<Text>
								Classification is being initiated. Please wait as the process
								begins. You will be redirected to the classification screen
								shortly.
							</Text>
						) : (
							<Text>
								Normalization is completed. You will be redirected to the vendor
								edit screen shortly.
							</Text>
						)}
					</DropZoneArea>
				) : (
					currentNormalizationStep === VENDOR_UPLOAD_STATUS.NO_DATA && (
						<Dropzone
							ref={this.dropzoneRef}
							onDrop={this.onFileSelected}
							style={{}}
							noKeyboard
						>
							{({ getRootProps, getInputProps }) => (
								<DropWrapper {...getRootProps()}>
									<input {...getInputProps()} />
									<DropZoneArea>
										<React.Fragment>{this.renderInitialBlock()}</React.Fragment>
									</DropZoneArea>
								</DropWrapper>
							)}
						</Dropzone>
					)
				)}
			</React.Fragment>
		);
	}
}

export default props => <Index {...props} />;
